import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledTooltip } from 'reactstrap';
import PitchTextEditor from './PitchTextEditor';
import PitchLibrary from './PitchLibrary';
import PitchVideoSection from './PitchVideoSection';
import AttachEntityModal from './attach_entity/AttachEntityModal';
import ActionButtons from './ActionButtons';
import FileInput from '../../file_input';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS
import stripHtmlToVisibleText from '../../../javascript/stripHtmlToVisibleText';
import { plainTextToComposerHtml, looksLikeHtml } from '../../../javascript/plainTextToComposerHtml';

function PitchComposer({
                         addExistingEntityToPitch,
                         addNewEntityToPitch,
                         attachedEntities,
                         attachedExistingFiles,
                         conversationOnly,
                         deleteExistingEntityFromPitch,
                         deleteNewEntity,
                         pitchText: pText,
                         pitchPublicizable,
                         setAttachedFileUploads,
                         setCompositionStarted,
                         setPitchPublicizable,
                         updateAndSendPitch,
                         requestId,
                         restrictedFreeUser,
                         sourceRequestType,
                         pitchId,
                         opportunityDetails,
                         videoPitchRecordingEnabled,
                         aiDetectorEnabled,
                       }) {
  // Old plain drafts would collapse their \n breaks; no content_format here, so sniff.
  const [pitchText, setPitchText] = useState(() => (
    looksLikeHtml(pText) ? pText : plainTextToComposerHtml(pText)
  ));
  const [pitchTextEdited, setPitchTextEdited] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [hasAttachedVideo, setHasAttachedVideo] = useState(false);

  const MIN_PITCH_CHARS = 10;
  // Mirrors V2's Interact::Pitch::PITCH_TEXT_MAX_LENGTH (DB-enforced there); V1Link truncates synced pitches at it.
  const MAX_PITCH_CHARS = 10000;

  // Rich text: limits count VISIBLE characters (same strip-regex as the server's dual limits).
  const strippedLength = stripHtmlToVisibleText(pitchText).length;
  const charCount = strippedLength;
  const overLimit = charCount > MAX_PITCH_CHARS;
  // A video recorded on Qwoted stands in for the text minimum (product rule, 2026-08-27); file
  // attachments never do. The server re-checks the video with V2 before accepting a text-less send.
  const textSatisfied = strippedLength >= MIN_PITCH_CHARS || hasAttachedVideo;
  // videoUploading holds Submit until the direct-to-Cloudinary upload + attach settle — submitting
  // mid-upload would send the pitch without the video the user just recorded.
  const isSubmitDisabled = isLoading || videoUploading || !textSatisfied || overLimit;

  // Callback for editing pitch text
  const handlePitchTextEdit = (text) => {
    setPitchTextEdited(true);
    setPitchText(text);
    setCompositionStarted(true);
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      // An emptied editor holds `<br>`-style markup; a video-only pitch must reach the server as ''.
      await updateAndSendPitch(strippedLength === 0 ? '' : pitchText);
    } finally {
      setIsLoading(false);
    }
  };

  const discardEdits = () => {
    setPitchTextEdited(false);
    setPitchText('');
    setCompositionStarted(false);
    // onDiscard();
  };

  // Toggle the pitchPublicizable state
  const togglePitchPublicizable = () => {
    setPitchPublicizable(!pitchPublicizable);
  };

  return (
    <div>
      {attachedEntities?.length > 0 && (
        <PitchLibrary
          attachedEntities={attachedEntities}
          setPitchText={setPitchText}
          pitchTextEdited={pitchTextEdited}
          discardEdits={discardEdits}
        />
      )}

      <PitchTextEditor
        pitchText={pitchText}
        setPitchText={handlePitchTextEdit}
        pitchTextEdited={pitchTextEdited}
        attachedEntities={attachedEntities}
        deleteExistingEntityFromPitch={deleteExistingEntityFromPitch}
        deleteNewEntity={deleteNewEntity}
        requestId={requestId}
        sourceRequestType={sourceRequestType}
        pitchId={pitchId}
        overLimit={overLimit}
        charCount={charCount}
        charLimit={MAX_PITCH_CHARS}
        aiDetectorEnabled={aiDetectorEnabled}
      />

      <div className="mb-2" />

      <AttachEntityModal
        addExistingEntityToPitch={addExistingEntityToPitch}
        addNewEntityToPitch={addNewEntityToPitch}
        attachedEntities={attachedEntities}
      />
      <div className="mb-3">
        <FileInput
          multiple
          existingFiles={attachedExistingFiles}
          onChange={(files) => setAttachedFileUploads(files)}
        />
      </div>
      {videoPitchRecordingEnabled && (
        <PitchVideoSection
          pitchId={pitchId}
          opportunityDetails={opportunityDetails}
          onUploadingChange={setVideoUploading}
          onAttachedChange={setHasAttachedVideo}
        />
      )}
      {!conversationOnly
        && (
        <div className="mb-2 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="pitchPublicizableCheck"
            checked={pitchPublicizable}
            onChange={togglePitchPublicizable}
          />
          <label className="form-check-label" htmlFor="pitchPublicizableCheck">
            Auto-summarize: Generates an automated summary of your perspective and adds it to your profile.
            <UncontrolledTooltip target="amplify-tooltip">
              Maximize your visibility by sending out a pitch summary and increase chances of direct
              outreach from targeted reporters working on tight deadlines.
            </UncontrolledTooltip>
            <span id="amplify-tooltip">
            &nbsp;<i className="fa-solid fa-circle-info text-info" />
          </span>
          </label>
        </div>
      )}
      { restrictedFreeUser && (
        <div>
          <ActionButtons
            onClick={handleSubmit}
            onSecondaryClick={discardEdits}
            isLoading={isLoading}
            restrictedFreeUser
            disabled={isSubmitDisabled}
          />
          <p style={{ fontStyle: 'italic', fontSize: '10px' }}> If you schedule a pitch, you will receive a confirmation email once it has been submitted.</p>
        </div>
      )}
      { !restrictedFreeUser && (
        <ActionButtons
          onClick={handleSubmit}
          onSecondaryClick={discardEdits}
          isLoading={isLoading}
          disabled={isSubmitDisabled}
        />
      )}

    </div>
  );
}

PitchComposer.propTypes = {
  addExistingEntityToPitch: PropTypes.func.isRequired,
  addNewEntityToPitch: PropTypes.func.isRequired,
  attachedEntities: PropTypes.arrayOf(PropTypes.object).isRequired,
  attachedExistingFiles: PropTypes.arrayOf(PropTypes.object),
  conversationOnly: PropTypes.bool.isRequired,
  deleteExistingEntityFromPitch: PropTypes.func.isRequired,
  deleteNewEntity: PropTypes.func.isRequired,
  // onDiscard: PropTypes.func.isRequired,
  pitchText: PropTypes.string.isRequired,
  pitchPublicizable: PropTypes.bool.isRequired,
  setAttachedFileUploads: PropTypes.func.isRequired,
  setCompositionStarted: PropTypes.func.isRequired,
  setPitchPublicizable: PropTypes.func.isRequired,
  updateAndSendPitch: PropTypes.func.isRequired,
  requestId: PropTypes.number.isRequired,
  restrictedFreeUser: PropTypes.bool,
  sourceRequestType: PropTypes.string,
  pitchId: PropTypes.string,
  opportunityDetails: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.string,
  }),
  videoPitchRecordingEnabled: PropTypes.bool,
  aiDetectorEnabled: PropTypes.bool,
};

PitchComposer.defaultProps = {
  attachedExistingFiles: [],
  restrictedFreeUser: false,
  sourceRequestType: '',
  pitchId: undefined,
  opportunityDetails: null,
  videoPitchRecordingEnabled: false,
  aiDetectorEnabled: false,
};

export default PitchComposer;
